import { createContext } from "react";
import type { SignalActionsAdapter, SignalFeedAdapter } from "./types";
import { createMockAdapters } from "./mock/mockAdapters";
import {
  LiveSignalActionsAdapter,
  LiveSignalFeedAdapter,
} from "./live/liveAdapters";

/**
 * Adapter selection + context (spec §8). Kept in a component-free module so the
 * Provider component (adapters.tsx) and the hooks (use-adapters.ts) can each
 * live in files that satisfy react-refresh's only-export-components rule. This
 * mirrors the repo's auth split (AuthProvider.tsx + use-auth.ts).
 */

export interface SignalAdapters {
  feed: SignalFeedAdapter;
  actions: SignalActionsAdapter;
}

export type AdapterMode = "mock" | "live";

/**
 * Adapter mode is env-driven (W1 live wiring). `VITE_ADAPTER_MODE=live` binds
 * the feed + actions to the deployed v08 Edge Functions (get_signal_feed and
 * the ownership/action-ledger mutation functions); anything else — including
 * unset, which is the default for tests and the mock screenshot harness — keeps
 * the in-memory mock adapters.
 */
export const ADAPTER_MODE: AdapterMode =
  import.meta.env.VITE_ADAPTER_MODE === "live" ? "live" : "mock";

function selectAdapters(): SignalAdapters {
  if (ADAPTER_MODE === "live") {
    return {
      feed: new LiveSignalFeedAdapter(),
      actions: new LiveSignalActionsAdapter(),
    };
  }
  return createMockAdapters();
}

export const defaultAdapters = selectAdapters();

export const AdapterContext = createContext<SignalAdapters>(defaultAdapters);
